import {
  Children,
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import isPropValid from '@emotion/is-prop-valid';
import Color from 'color';
import {
  ThemeProvider as OriginalThemeProvider,
  StyleSheetManager,
} from 'styled-components';

import type {ColorThemeID, Theme, Themes} from './theme';

import {platform} from '../utils/platform';
import {GlobalStyle} from './globalStyles';
import {themeFactory} from './theme';
import {appendStyle, generateCssTheme} from './utils/cssTheme';

type AccentColor = Record<ColorThemeID, string>;

type ContextValue = {
  setAccentColor: (accentColor: AccentColor, accentSecondary?: boolean) => void;
  setTheme: (themeId: ColorThemeID) => void;
  theme: Theme;
  themes: Record<ColorThemeID, Theme>;
};

export const ThemeContext = createContext<ContextValue>({
  setAccentColor: () => {},
  setTheme: () => {},
  theme: themeFactory('light'),
  // @ts-expect-error: will be set on mount
  themes: {},
});

type Props = PropsWithChildren;

export const ThemeProvider = (props: Props) => {
  const [currentThemeId, setCurrentThemeId] = useState<ColorThemeID>('light');
  const [accentColor, setAccentColor] = useState<AccentColor>();

  const themes = useRef<Themes>({
    dark: themeFactory('dark'),
    light: themeFactory('light'),
  });

  useEffect(() => {
    if (platform.isWeb) {
      appendStyle(
        generateCssTheme(currentThemeId, themes.current),
        'css-theme',
      );
    }
  }, [currentThemeId, themes]);

  const setAccentColorCssVars = useCallback(
    (color: AccentColor) => {
      if (platform.isNative) {
        return;
      }

      const colors = {
        '--current-accent-color': color[currentThemeId],
        '--current-accent-color-rgb': Color(color[currentThemeId])
          .array()
          .join(),
        '--dark-accent-color': color.dark,
        '--dark-accent-color-rgb': Color(color.dark).array().join(),
        '--light-accent-color': color.light,
        '--light-accent-color-rgb': Color(color.light).array().join(),
      };

      const css = Object.entries(colors)
        .map(([key, value]) => `${key}: ${value};`)
        .join('');

      appendStyle(
        `:root {
          ${css}
        }`,
        'accent-colors',
      );
    },
    [currentThemeId],
  );

  const setAccentColorCb: ContextValue['setAccentColor'] = useCallback(
    (color, accentForSecondaryColors = false) => {
      themes.current[currentThemeId] = themeFactory(currentThemeId, {
        accentColor: color[currentThemeId],
        accentForSecondaryColors,
      });

      if (platform.isWeb) {
        appendStyle(
          generateCssTheme(currentThemeId, themes.current),
          'css-theme',
        );
      }

      setAccentColorCssVars(color);
      setAccentColor(color);
    },
    [setAccentColorCssVars, currentThemeId],
  );

  const contextValue = useMemo(() => {
    return {
      setAccentColor: setAccentColorCb,
      setTheme: setCurrentThemeId,
      theme: themes.current[currentThemeId],
      themes: themes.current,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThemeId, setAccentColorCb, accentColor]);

  return platform.isWeb ? (
    <ThemeContext value={contextValue}>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <OriginalThemeProvider theme={contextValue.themes[currentThemeId]}>
          {Children.only(props.children)}
          <GlobalStyle theme={contextValue.themes[currentThemeId]} />
        </OriginalThemeProvider>
      </StyleSheetManager>
    </ThemeContext>
  ) : (
    <ThemeContext value={contextValue}>
      <OriginalThemeProvider theme={contextValue.themes[currentThemeId]}>
        {Children.only(props.children)}
      </OriginalThemeProvider>
    </ThemeContext>
  );
};

/**
 * This implements the default behavior from styled-components v5
 * @see https://styled-components.com/docs/faqs#what-do-i-need-to-do-to-migrate-to-v6
 */
function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }

  // For other elements, forward all props
  return true;
}
